import { makeStyles } from "@material-ui/core";

/**
 * Webinar-only additions on top of the shared OfferCheckout styles
 * (offerCheckout.styles.js): the "Why Join" content block and the mobile
 * sticky bottom bar. Brand hexes mirror offerCheckout.styles.js exactly —
 * no new palette.
 */
const BRAND = "#04cf66";
const BRAND_HOVER = "#03b85a";
const BORDER = "#E5E5E5";

export const useWebinarStyles = makeStyles((theme) => ({
  /* ---- top sticky countdown banner ---- */
  topBanner: {
    position: "sticky",
    top: 0,
    zIndex: 1300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    backgroundColor: "#0E1A14",
    color: "#ffffff",
    borderBottom: `2px solid ${BRAND}`,
    padding: theme.spacing(1, 2),
    fontSize: "14px",
    fontWeight: 700,
    textAlign: "center",
    lineHeight: 1.3,
  },
  topBannerIcon: { color: BRAND, fontSize: 18, flexShrink: 0 },
  // countdown digits — brand green accent against the white label text
  topBannerTimer: { color: BRAND, fontWeight: 800, fontVariantNumeric: "tabular-nums" },

  /* ---- top card polish ---- */
  // Gradient top accent strip on the plan card.
  cardAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_HOVER} 100%)`,
  },
  // "● Live Masterclass" eyebrow above the title.
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: "#236E34",
    backgroundColor: "#E3FFCC",
    border: `1px solid ${BRAND}55`,
    borderRadius: "20px",
    padding: theme.spacing(0.5, 1.25),
    marginBottom: theme.spacing(1.5),
  },
  liveDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: BRAND,
    boxShadow: `0 0 0 0 ${BRAND}99`,
    animation: "$livePulse 1.6s infinite",
  },
  "@keyframes livePulse": {
    "0%": { boxShadow: `0 0 0 0 ${BRAND}80` },
    "70%": { boxShadow: `0 0 0 6px ${BRAND}00` },
    "100%": { boxShadow: `0 0 0 0 ${BRAND}00` },
  },
  // center the top card content (heading + date pill) + match the 24px
  // inter-card gap used by the form/why-join cards (planCard defaults to 16).
  cardCenter: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  // "Hosted by" block at the TOP of the Why-Join card (divider below it).
  hostRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${BORDER}`,
  },
  hostAvatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  hostAvatarFallback: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: BRAND,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "17px",
  },
  hostLabel: { fontSize: "12px", color: "#696969", fontWeight: 600 },
  hostName: { fontSize: "15px", fontWeight: 700, color: "#000000", lineHeight: 1.3 },
  hostTitle: { fontSize: "13px", color: "#696969", lineHeight: 1.3 },
  // date/time pill under the heading in the top card
  cardDate: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.25),
    fontSize: "14px",
    fontWeight: 600,
    color: "#236E34",
    backgroundColor: "#E3FFCC",
    border: `1px solid ${BRAND}55`,
    borderRadius: "8px",
    padding: theme.spacing(0.6, 1.25),
  },
  // scarcity line under the price (a genuine checkout drop-off reducer)
  urgencyText: {
    marginTop: theme.spacing(1),
    fontSize: "13px",
    fontWeight: 700,
    color: "#D7263D",
  },
  // compact date/time line in the form header (time "fitted elsewhere")
  formDate: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
    fontSize: "13px",
    fontWeight: 500,
    color: "#696969",
  },
  // Hide an element on mobile (the form's big CTA — the sticky bar covers it).
  hideOnMobile: {
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  // Gap below the form, matching the inter-card spacing.
  formGap: {
    marginBottom: theme.spacing(2),
  },

  /* ---- Coupon field (opt-in per webinar) ---- */
  couponBox: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  couponRow: {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
  },
  couponInput: {
    flex: 1,
    minWidth: 0,
    height: 44,
    padding: "0 12px",
    fontSize: 14,
    textTransform: "uppercase",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    outline: "none",
    color: "#1a1a1a",
    background: "#fff",
  },
  couponButton: {
    height: 44,
    whiteSpace: "nowrap",
    border: "1px solid #04cf66",
    color: "#04cf66",
    fontWeight: 600,
    textTransform: "none",
  },
  couponMsgOk: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 600,
    color: "#236E34",
  },
  couponMsgErr: {
    marginTop: 6,
    fontSize: 13,
    color: "#d23f3f",
  },

  /* ---- "Why Join" block ---- */
  whyJoinCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    border: `1px solid ${BORDER}`,
  },
  whyJoinHeader: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "18px",
    fontWeight: 700,
    color: "#000000",
    lineHeight: 1.3,
  },
  whyJoinSub: {
    fontSize: "14px",
    color: "#696969",
    margin: theme.spacing(0.5, 0, 1.5),
  },
  /* ---- webinar date/time pill ---- */
  dateTimePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    backgroundColor: "#E3FFCC",
    color: "#236E34",
    border: `1px solid ${BRAND}55`,
    borderRadius: "8px",
    padding: theme.spacing(0.75, 1.25),
    fontSize: "13px",
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },

  /* ---- mobile sticky bottom bar ---- */
  // Spacer so the fixed bar never overlaps the last form field on mobile.
  stickySpacer: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      height: "84px",
    },
  },
  stickyBar: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      // Column so the coupon row sits on top of the price + Pay row.
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(0.5),
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1200,
      backgroundColor: "#ffffff",
      borderTop: `1px solid ${BORDER}`,
      boxShadow: "0 -4px 12px rgba(0,0,0,0.10)",
      padding: theme.spacing(1.25, 2),
      // iOS safe-area so the CTA isn't under the home indicator.
      paddingBottom: `calc(${theme.spacing(1.25)}px + env(safe-area-inset-bottom))`,
    },
  },
  // The price + Pay-Now row inside the sticky bar (kept as its own row so the
  // coupon can stack above it).
  stickyPayRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  stickyPriceWrap: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.1,
  },
  stickyPriceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(0.75),
  },
  stickyPrice: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "22px",
    fontWeight: 700,
    color: "#236E34",
  },
  stickyMrp: {
    fontSize: "13px",
    textDecoration: "line-through",
    color: "#9e9e9e",
  },
  stickyInclGst: {
    fontSize: "11px",
    color: "#9e9e9e",
    fontWeight: 500,
  },
  stickyHint: {
    fontSize: "11px",
    color: "#696969",
    marginTop: "2px",
  },
  stickyButton: {
    backgroundColor: BRAND,
    color: "#ffffff",
    "&:hover": { backgroundColor: BRAND_HOVER },
    "&:disabled": { backgroundColor: BRAND, opacity: 0.7, color: "#ffffff" },
    fontWeight: 600,
    fontSize: "15px",
    textTransform: "none",
    borderRadius: "8px",
    padding: theme.spacing(1.25, 3),
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(4,207,102,0.30)",
      // gentle pulse to draw the eye to the primary action
    animation: "$payPulse 1.6s ease-in-out infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  "@keyframes payPulse": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.04)" },
    "100%": { transform: "scale(1)" },
  },
}));
